/**
 * Background Scheduler — WHEN a job should run.
 *
 * Schedules have no permissions of their own; they inherit the job's.
 *
 * There is no global "list all schedules" endpoint (only GET /{jobId}/schedules),
 * so the list is assembled per job. That is an N+1 fan-out, fine for an admin
 * screen with jobs in the tens; it would need a backend endpoint at real scale.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppNavigate } from '../../../hooks/useAppNavigate';
// InputNumber and ConfirmDialog come straight from PrimeReact — sharedUi has no
// wrapper for either. Everything else here goes through the shared wrappers.
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { InputSwitch } from 'sharedUi/InputSwitch';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { Tag } from 'sharedUi/Tag';
import { formatTimestamp } from 'sharedUi/dateUtils';
import SaasDataTable, { type SaasColumn } from '../../../components/common/saas-data-table';
import { ListCount } from '../../../components/common/list-count';
import { backgroundJobService } from '../../../services/background-job.service';
import { ProjectCell } from '../../../components/common/project-cell';
import { useAccessibleProjects, projectOptions } from '../../../hooks/useProjectScope';
import type {
  BackgroundJobSchedule,
} from '../../../types/background-job.types';
import { POLL_INTERVAL_SECONDS, describeTiming, fromNaiveIso } from './shared';
import { useToastApi } from '../../../hooks/use-toast-api';
import { useJobPermissions } from './use-job-permissions';
import { useScheduleTypes } from './use-job-value-sets';

const SchedulesTab = () => {
  const { canCreate, canUpdate, canDelete, canToggle } = useJobPermissions();
  const navigate = useAppNavigate();
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();

  const [jobFilter, setJobFilter] = useState('');
  // The list is composed in the browser (one query per job), so its filters are
  // applied here too — see allSchedules below.
  const [typeFilter, setTypeFilter] = useState('');
  const [appFilter, setAppFilter] = useState('');
  const { projects, canScopeByProject } = useAccessibleProjects();
  const { options: scheduleTypeOptions } = useScheduleTypes();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState<{ field: string | null; order: 1 | -1 | null }>({ field: 'nextRunAt', order: 1 });


  // every job, so schedules can be listed and named without a second lookup.
  //
  // The project filter is applied HERE, to the jobs, and the schedules follow.
  // That is not a shortcut: a schedule carries no appId of its own, and the
  // backend scopes them the same way — background_job_dashboard_service resolves
  // `find_ids_in_scope(scope)` on the jobs and filters schedules by those ids,
  // noting that "schedules and runs carry no project of their own". Filtering the
  // jobs query is the client-side shape of exactly that.
  const jobsQ = useQuery({
    queryKey: ['bg-jobs', 'all-for-schedules', appFilter],
    queryFn: () => backgroundJobService.listJobs({
      page: 1, page_size: 200, appId: appFilter || undefined,
    }),
    staleTime: 60_000,
  });
  // memoised: it feeds a useMemo below, and a fresh [] each render would re-run it
  const jobs = useMemo(() => jobsQ.data?.items ?? [], [jobsQ.data]);
  const targetJobs = jobFilter ? jobs.filter((j) => j._id === jobFilter) : jobs;

  const scheduleQs = useQueries({
    queries: targetJobs.map((j) => ({
      queryKey: ['bg-schedules', j._id],
      queryFn: () => backgroundJobService.listSchedules(j._id),
      staleTime: 30_000,
    })),
  });

  const loading = jobsQ.isFetching || scheduleQs.some((q) => q.isFetching);

  const allSchedules: BackgroundJobSchedule[] = useMemo(() => {
    const byId = new Map(jobs.map((j) => [j._id, j]));
    return scheduleQs
      .flatMap((q) => q.data ?? [])
      .filter((s) => !typeFilter || s.scheduleType === typeFilter)
      .map((s) => ({ ...s, jobName: byId.get(s.jobId)?.jobName, jobKey: byId.get(s.jobId)?.jobKey,
                     // A schedule stores no appId; the backend scopes it through its
                     // job, so the column shows the job's project for the same reason.
                     appId: byId.get(s.jobId)?.appId }))
      .sort((a, b) => {
        // the list is composed client-side, so sorting is too
        const f = sort.field || 'nextRunAt';
        const dir = sort.order ?? 1;
        const va = (a as unknown as Record<string, unknown>)[f];
        const vb = (b as unknown as Record<string, unknown>)[f];
        // a missing value goes last whichever way the column is ordered
        if (va == null || vb == null) return va == null ? (vb == null ? 0 : 1) : -1;
        return String(va).localeCompare(String(vb)) * dir;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleQs.map((q) => q.dataUpdatedAt).join(','), jobs, sort, typeFilter]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['bg-schedules'] });

  const toggleM = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => backgroundJobService.toggleSchedule(id, on),
    onSuccess: (_d, v) => {
      ok(v.on ? 'Enabled — the next run is computed from now, not from the old value' : 'Disabled — history is preserved');
      invalidate();
    },
    onError: (e) => fail(e),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => backgroundJobService.deleteSchedule(id),
    onSuccess: () => { ok('Schedule deleted'); invalidate(); },
    onError: (e) => fail(e),
  });

  // Create and edit are PAGES (see schedule-form.tsx), not a modal on this tab —
  // the shape every other master uses, so leaving with unsaved edits is guarded by
  // useNavigationBlocker instead of being lost to a backdrop click. The timing
  // validation, the type switch and the submit that used to live here moved with
  // the form; what stayed behind referenced state that no longer existed.
  //
  // Creating carries the tab's current job filter, so adding a schedule while
  // looking at one job does not make you pick that job again on the next screen.
  const openCreate = () => navigate(
    jobFilter
      ? `/background-jobs/schedules/create?jobId=${jobFilter}`
      : '/background-jobs/schedules/create');

  // The jobId is passed on edit too, and is not decoration: there is no
  // GET /schedules/{id}, so the form finds a schedule by listing its owning job's.
  // Without the hint it has to list EVERY job's schedules and search them.
  const openEdit = (s: BackgroundJobSchedule) =>
    navigate(`/background-jobs/schedules/${s._id}/edit?jobId=${s.jobId}`);

  const columns: SaasColumn<BackgroundJobSchedule>[] = useMemo(() => [
    {
      field: 'jobName', header: 'Job', sortable: true, hideable: false, style: { minWidth: 200 },
      body: (s) => (
        <div>
          <div className="text-sm font-medium text-ink">{s.jobName || '—'}</div>
          <div className="font-mono text-[11px] text-gray-400">{s.jobKey}</div>
        </div>
      ),
    },
    { field: 'scheduleName', header: 'Schedule', sortable: true, filter: true, style: { minWidth: 140 } },
    {
      field: 'scheduleType', header: 'Type', style: { width: 140 },
      body: (s) => <Tag value={s.scheduleType} severity={s.scheduleType === 'CRON' ? 'info' : s.scheduleType === 'INTERVAL' ? 'warning' : 'secondary'} />,
    },
    {
      field: 'timing', header: 'When', style: { minWidth: 150 },
      body: (s) => <span className="font-mono text-xs">{describeTiming(s)}</span>,
    },
    {
      field: 'nextRunAt', header: 'Next Run', sortable: true, style: { minWidth: 175 },
      body: (s) => (s.nextRunAt
        ? <span className="text-xs">{formatTimestamp(s.nextRunAt)}</span>
        // the poller selects on `nextRunAt <= now`, so a null one is never picked up.
        // Enabled + no next run reads as healthy but means "will never fire".
        : <span className="text-xs text-amber-600 dark:text-amber-400" title="The poller selects on nextRunAt, so this schedule will never fire. A ONCE time that has passed clears it — set a future time to recompute.">
            <i className="pi pi-exclamation-triangle mr-1" style={{ fontSize: '0.7rem' }} />no next run
          </span>),
    },
    {
      field: 'lastRunAt', header: 'Last Run', style: { minWidth: 165 },
      body: (s) => <span className="text-xs text-gray-500">{s.lastRunAt ? formatTimestamp(s.lastRunAt) : 'never'}</span>,
    },
    {
      field: 'appId', header: 'Project', style: { minWidth: 150 },
      body: (s: BackgroundJobSchedule & { appId?: string | null }) =>
        <ProjectCell appId={s.appId} projects={projects} />,
    },
    {
      /* The on/off switch lives here rather than behind an icon in Actions: enabling
         and disabling is the routine operational act on this screen, so it reads as
         state you flip, not a command you invoke. It calls the TOGGLE endpoint, which
         is gated on its own key — see BACKGROUND_JOB_SCHEDULE.TOGGLE. */
      field: 'isScheduleEnabled', header: 'Enabled', align: 'center', style: { width: 150 },
      body: (s) => {
        // a ONCE schedule disables itself after firing — that is "completed", not "someone turned it off"
        const completed = !s.isScheduleEnabled && s.scheduleType === 'ONCE' && !!s.lastRunAt;
        const busy = toggleM.isPending && toggleM.variables?.id === s._id;
        // Due in the past and still enabled: the poller should have taken it and did
        // not. Usually the engine is not running at all (JOB_ENGINE_ENABLED). One poll
        // interval of slack keeps a schedule from flickering red the moment it comes due.
        const due = fromNaiveIso(s.nextRunAt);
        const overdue = s.isScheduleEnabled && !!due
          && due.getTime() < Date.now() - POLL_INTERVAL_SECONDS * 1000;
        const title = !canToggle
          ? 'You do not have permission to enable or disable schedules.'
          : completed
            // flipping it on alone would leave it enabled with a past run-at, so
            // nextRunAt stays empty and it still never fires — both fields must change together
            ? 'This one-off already ran. Open Edit to set a new run time and re-enable it together.'
            : s.isScheduleEnabled
              ? 'Disable — reversible, and the run history is kept.'
              : 'Enable — the next run is computed from now, not from the old value.';
        return (
          <div className="flex items-center justify-center gap-2" title={title}>
            <InputSwitch
              checked={!!s.isScheduleEnabled}
              disabled={!canToggle || completed || busy}
              onChange={(e) => toggleM.mutate({ id: s._id, on: !!e.value })}
            />
            {/* only the states the switch cannot express get a tag; on/off is already visible */}
            {completed && <Tag value="Completed" severity="info" />}
            {!completed && s.isScheduleEnabled && !s.nextRunAt && <Tag value="Won't fire" severity="warning" />}
            {overdue && (
              <span title="This was due but no run was recorded. The job engine is most likely not running — it starts only when JOB_ENGINE_ENABLED is set on the API.">
                <Tag value="Overdue" severity="danger" />
              </span>
            )}
          </div>
        );
      },
    },
    {
      field: 'actions', header: 'Actions', hideable: false, align: 'center', style: { width: 160 },
      body: (s) => (
        <div className="flex items-center justify-center gap-1">
          {/* The schedule's own page: timing, next occurrences and the runs it produced. */}
          <Button aria-label="View" icon="pi pi-eye" text rounded severity="success" tooltip="View"
            onClick={() => navigate(`/background-jobs/schedules/${s._id}?jobId=${s.jobId}`)} />
          {canUpdate && <Button aria-label="Edit" icon="pi pi-pencil" text rounded tooltip="Edit" onClick={() => openEdit(s)} />}
          {canDelete && (
            <Button icon="pi pi-trash" text rounded severity="danger" tooltip="Delete"
              onClick={() => confirmDialog({
                message: `Delete schedule "${s.scheduleName}"? Disabling it is reversible from the UI; deleting is not.`,
                header: 'Delete schedule', icon: 'pi pi-exclamation-triangle',
                acceptClassName: 'p-button-danger',
                accept: () => deleteM.mutate(s._id),
              })} />
          )}
        </div>
      ),
    },
    // isPending/variables are listed so the in-flight switch actually re-renders as busy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canUpdate, canDelete, canToggle, toggleM.isPending, toggleM.variables?.id, canScopeByProject, projects]);

  const rows = allSchedules.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      {toast}
      <ConfirmDialog />

      <SaasDataTable
        tableId="background-schedules"
        columns={columns}
        data={rows}
        loading={loading}
        dataKey="_id"
        emptyMessage="No schedules. A job with no schedule is still valid — it runs only via Run now."
        first={(page - 1) * pageSize}
        rows={pageSize}
        totalRecords={allSchedules.length}
        onPage={(e: { first: number; rows: number }) => { setPageSize(e.rows); setPage(Math.floor(e.first / e.rows) + 1); }}
        rowsPerPageOptions={[10, 25, 50]}
        sortField={sort.field ?? undefined}
        sortOrder={sort.order ?? undefined}
        onSort={(e: { sortField: string; sortOrder: 1 | -1 | null }) => { setSort({ field: e.sortField, order: e.sortOrder }); setPage(1); }}
        filters={{}}
        onFilter={() => { /* no server-side filter on this composed list */ }}
        testId="BackgroundSchedules"
        toolbarStart={
          <div className="flex flex-wrap items-center gap-2">
            {canScopeByProject && (
              <Dropdown
                value={appFilter}
                options={projectOptions(projects)}
                optionLabel="label" optionValue="value" placeholder="All Projects"
                showClear={!!appFilter} style={{ minWidth: 170 }}
                onChange={(e: { value: string }) => {
                  // The job filter names a job from the OLD project, so it is
                  // cleared: left set, it would intersect to nothing and the tab
                  // would read as empty rather than as filtered.
                  setAppFilter(e.value || '');
                  setJobFilter('');
                  setPage(1);
                }}
                data-testid="BackgroundSchedules.ProjectFilter"
              />
            )}
            <Dropdown
              value={jobFilter}
              options={[{ label: 'All Jobs', value: '' }, ...jobs.map((j) => ({ label: `${j.jobName} (${j.jobKey})`, value: j._id }))]}
              optionLabel="label" optionValue="value" placeholder="All Jobs" filter resetFilterOnHide
              showClear={!!jobFilter} style={{ minWidth: 240 }}
              onChange={(e: { value: string }) => { setJobFilter(e.value || ''); setPage(1); }}
            />
            <Dropdown
              value={typeFilter}
              options={[{ label: 'All Types', value: '' }, ...scheduleTypeOptions]}
              optionLabel="label" optionValue="value" placeholder="All Types"
              showClear={!!typeFilter} style={{ minWidth: 150 }}
              onChange={(e: { value: string }) => { setTypeFilter(e.value || ''); setPage(1); }}
              data-testid="BackgroundSchedules.TypeFilter"
            />
          </div>
        }
        toolbarEnd={
          <>
            {/* The row count is the composed list's length, not one query's
                total: this tab fans out a schedules query per job and merges
                them, so no single response knows how many there are. */}
            <ListCount total={allSchedules.length} onRefresh={invalidate}
              loading={loading} testId="BackgroundSchedules.Refresh" />
            {canCreate && jobs.length > 0 && (
              <Button label="Add Schedule" icon="pi pi-plus" onClick={openCreate} />
            )}
          </>
        }
      />

      {/* The create/edit modal that stood here is gone: both are PAGES now
          (schedule-form.tsx), reached by openCreate/openEdit above. Its state
          went with it but the JSX did not, so the tab crashed on every render
          the same way the jobs tab did. */}
    </>
  );
};

export default SchedulesTab;
